import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusCircle } from "lucide-react";

import { ApiError } from "../../lib/api";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { Button, Input, Select, Textarea, useDescarteProtegido, useHayCambios } from "../../components/ui";
import { fetchContratos, type ContratoOperativo } from "../socios/api";
import { crearLectura, type TipoLectura } from "./api";

/** Normaliza para búsqueda: minúsculas sin acentos. */
function normalizar(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Alta manual de una lectura desde la oficina (recorrido R1).
 *
 * Muchos comités leen en papel y digitan después: hasta este lote la única
 * entrada de lecturas era la PWA de terreno, así que una casa que el operador
 * no cargó no tenía forma de entrar al sistema.
 */
export function IngresarLecturaModal({
  mes,
  anio,
  contratosConLectura,
  onClose,
}: {
  mes: number;
  anio: number;
  /** Contratos que ya tienen lectura activa en el período: no se ofrecen. */
  contratosConLectura: ReadonlySet<number>;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [busqueda, setBusqueda] = useState("");
  const [contratoId, setContratoId] = useState<number | null>(null);
  const [valor, setValor] = useState("");
  const [tipo, setTipo] = useState<TipoLectura>("Real");
  const [motivoEstimada, setMotivoEstimada] = useState("");

  const contratosQ = useQuery({ queryKey: ["contratos"], queryFn: fetchContratos });

  const candidatos = useMemo(() => {
    const todos = (contratosQ.data ?? []).filter(
      (c) => !contratosConLectura.has(c.id),
    );
    const q = normalizar(busqueda.trim());
    if (!q) return todos.slice(0, 50);
    const terms = q.split(/\s+/);
    return todos
      .filter((c) => {
        const hay = normalizar(
          `${c.socio_nombre} ${c.socio_rut} ${c.arranque_codigo ?? ""} ` +
            `${c.arranque_direccion ?? ""}`,
        );
        return terms.every((t) => hay.includes(t));
      })
      .slice(0, 50);
  }, [contratosQ.data, contratosConLectura, busqueda]);

  const elegido: ContratoOperativo | undefined = useMemo(
    () => (contratosQ.data ?? []).find((c) => c.id === contratoId),
    [contratosQ.data, contratoId],
  );

  const m = useMutation({
    mutationFn: () =>
      crearLectura({
        contrato_id: contratoId as number,
        mes,
        anio,
        lectura_actual: Number(valor),
        tipo_lectura: tipo,
        motivo_estimada: tipo === "Estimada" ? motivoEstimada.trim() : null,
      }),
    onSuccess: (lectura) => {
      toast.success(`Lectura cargada: ${lectura.consumo_m3} m³.`);
      void qc.invalidateQueries({ queryKey: ["lecturas"] });
      void qc.invalidateQueries({ queryKey: ["cierre"] });
      void qc.invalidateQueries({ queryKey: ["cierre-faltantes"] });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo cargar la lectura",
      ),
  });

  const valorNum = Number(valor);
  const valido =
    contratoId != null &&
    valor !== "" &&
    Number.isFinite(valorNum) &&
    valorNum >= 0 &&
    (tipo !== "Estimada" || motivoEstimada.trim().length >= 3);

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([busqueda, contratoId, valor, tipo, motivoEstimada]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    m.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Ingresar lectura"
      subtitle={`Período ${String(mes).padStart(2, "0")}/${anio}`}
      icon={PlusCircle}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={m.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!valido || m.isPending}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Guardando…" : "Guardar lectura"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">
          Para las casas que se leyeron en papel o que el operador no alcanzó a
          cargar. El período tiene que tener un cierre abierto.
        </p>

        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">
            Buscar el socio
          </span>
          <Input
            type="text"
            value={busqueda}
            autoFocus
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Nombre, RUT, arranque o dirección"
            className="mt-1"
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">
            Contrato sin lectura en el período
          </span>
          <Select
            value={contratoId ?? ""}
            onChange={(e) =>
              setContratoId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={contratosQ.isPending}
            className="mt-1"
          >
            <option value="">
              {contratosQ.isPending
                ? "Cargando…"
                : candidatos.length === 0
                ? "Sin contratos pendientes que coincidan"
                : "Elige un contrato"}
            </option>
            {candidatos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.socio_nombre} · {c.arranque_codigo ?? `contrato #${c.id}`}
                {c.arranque_direccion ? ` · ${c.arranque_direccion}` : ""}
              </option>
            ))}
          </Select>
          {elegido && (
            <span className="mt-1 block text-xs text-neutral-500">
              {elegido.socio_rut} · {elegido.arranque_direccion ?? "sin dirección"}
            </span>
          )}
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700">
              Lectura del medidor
            </span>
            <Input
              type="number"
              min={0}
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="mt-1"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700">Tipo</span>
            <Select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoLectura)}
              className="mt-1"
            >
              <option value="Real">Real</option>
              <option value="Estimada">Estimada (término medio)</option>
              <option value="Ajuste">Ajuste</option>
            </Select>
          </label>
        </div>

        {tipo === "Estimada" && (
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700">
              Motivo de la estimación
            </span>
            <Textarea
              rows={2}
              value={motivoEstimada}
              onChange={(e) => setMotivoEstimada(e.target.value)}
              placeholder="Ej: sin acceso al medidor, perro suelto"
              className="mt-1"
            />
            <span className="mt-1 block text-xs text-neutral-500">
              Para estimar muchas casas de una vez, usa &ldquo;Facturar por
              término medio&rdquo; en el detalle del cierre: calcula el promedio
              solo.
            </span>
          </label>
        )}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}
